import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// Helper to get default dates
const getDefaultStartDate = () => {
  const date = new Date()
  date.setDate(date.getDate() - 7)
  return date.toISOString().split('T')[0]
}

const getDefaultEndDate = () => {
  return new Date().toISOString().split('T')[0]
}

// Common filters shared across all pages
interface CommonFilters {
  countries: string[]
  operators: string[]
  productIds: number[]
  sources: string[]
  customerIds: number[]
  flow: string
  timezone: string
  startDate: string
  endDate: string
  granularity: 'hour' | 'day' | 'week'
}

// Funnel-specific filters
interface FunnelFilters {
  selectedCountry: string
  selectedOperator: string
  connectionType: 'ALL' | 'HE' | 'WIFI'
  selectedCustomer: string
  selectedProduct: string
  selectedReference: string
  selectedSource: string
}

interface FiltersState {
  // Common filters (shared across pages)
  filters: CommonFilters
  
  // Page-specific filters
  funnelFilters: FunnelFilters
  
  // Available options from API
  options: {
    countries: Array<{ code: string; name: string; flag: string }>
    operators: {
      list: Array<{ operator: string; country: string; label: string }>
      byCountry: Record<string, string[]>
    }
    products: Array<{
      id: number
      name: string
      country: string
      operator: string
      source: string | null
      customerId: number | null
      customerName: string
    }>
    sources: string[]
    customers: Array<{ id: number; name: string; shortName: string | null }>
    timezones: Array<{ value: string; label: string; offset: number }>
  }
  optionsLoaded: boolean
  
  // Actions - Common filters
  setFilter: <K extends keyof CommonFilters>(key: K, value: CommonFilters[K]) => void
  setFilters: (filters: Partial<CommonFilters>) => void
  clearFilters: () => void
  setOptions: (options: FiltersState['options']) => void
  setTimezone: (timezone: string) => void
  setDateRange: (startDate: string, endDate: string) => void
  setGranularity: (granularity: 'hour' | 'day' | 'week') => void
  
  // Actions - Funnel filters
  setFunnelFilter: <K extends keyof FunnelFilters>(key: K, value: FunnelFilters[K]) => void
  setFunnelFilters: (filters: Partial<FunnelFilters>) => void
  clearFunnelFilters: () => void
}

const defaultFilters: CommonFilters = {
  countries: [],
  operators: [],
  productIds: [],
  sources: [],
  customerIds: [],
  flow: '',
  timezone: 'America/Costa_Rica',
  startDate: getDefaultStartDate(),
  endDate: getDefaultEndDate(),
  granularity: 'day',
}

const defaultFunnelFilters: FunnelFilters = {
  selectedCountry: '',
  selectedOperator: '',
  connectionType: 'ALL',
  selectedCustomer: 'ALL',
  selectedProduct: 'ALL',
  selectedReference: 'ALL',
  selectedSource: 'ALL',
}

export const useFiltersStore = create<FiltersState>()(
  persist(
    (set) => ({
      filters: defaultFilters,
      funnelFilters: defaultFunnelFilters,
      options: {
        countries: [],
        operators: { list: [], byCountry: {} },
        products: [],
        sources: [],
        customers: [],
        timezones: [
          { value: 'America/Costa_Rica', label: 'Costa Rica (UTC-6)', offset: -6 },
          { value: 'America/Bogota', label: 'Colombia (UTC-5)', offset: -5 },
        ],
      },
      optionsLoaded: false,

      // Common filter actions
      setFilter: (key, value) =>
        set((state) => ({
          filters: { ...state.filters, [key]: value },
        })),

      setFilters: (filters) =>
        set((state) => ({
          filters: { ...state.filters, ...filters },
        })),

      clearFilters: () =>
        set((state) => ({
          filters: { 
            ...defaultFilters, 
            timezone: state.filters.timezone,
            startDate: state.filters.startDate,
            endDate: state.filters.endDate,
          },
        })),

      setOptions: (options) =>
        set({
          options,
          optionsLoaded: true,
        }),

      setTimezone: (timezone) =>
        set((state) => ({
          filters: { ...state.filters, timezone },
        })),

      setDateRange: (startDate, endDate) =>
        set((state) => ({
          filters: { ...state.filters, startDate, endDate },
        })),

      setGranularity: (granularity) =>
        set((state) => ({
          filters: { ...state.filters, granularity },
        })),

      // Funnel filter actions
      setFunnelFilter: (key, value) =>
        set((state) => ({
          funnelFilters: { ...state.funnelFilters, [key]: value },
        })),

      setFunnelFilters: (filters) =>
        set((state) => ({
          funnelFilters: { ...state.funnelFilters, ...filters },
        })),

      clearFunnelFilters: () =>
        set(() => ({
          funnelFilters: defaultFunnelFilters,
        })),
    }),
    {
      name: 'xafra-bi-filters',
      partialize: (state) => ({
        filters: state.filters,
        funnelFilters: state.funnelFilters,
      }),
    }
  )
)
